import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
    ClipboardList,
    History,
    MapPin,
    Pencil,
    Power,
    Trash2,
} from "lucide-react";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";

import { PageHeader } from "@/components/common/PageHeader";
import { QRDisplay } from "@/components/common/QRDisplay";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { assetsService } from "@/services/assets.service";
import { customersService } from "@/services/customers.service";
import { getErrorMessage } from "@/services/api";
import { formatDate, formatDateTime, mediaUrl } from "@/lib/utils";
import {
    OS_PRIORITY_LABELS,
    PHOTO_CATEGORIES,
    WO_STATUS_LABELS,
} from "@/config/constants";
import { RoleGate } from "@/components/auth/RoleGate";
import { WoStatusBadge } from "@/components/common/WoStatusBadge";
import { AssetTimeline } from "@/features/assets/AssetTimeline";
import { useAuth } from "@/contexts/AuthContext";

function Info({ label, value }) {
    return (
        <div>
            <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                {label}
            </div>
            <div className="text-sm text-foreground">{value ?? "—"}</div>
        </div>
    );
}

export default function AssetDetailsPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const qc = useQueryClient();
    const { user } = useAuth();
    const isAdmin = user?.role === "Administrator";

    const detail = useQuery({
        queryKey: ["asset", id],
        queryFn: () => assetsService.get(id),
    });
    const stats = useQuery({
        queryKey: ["asset-stats", id],
        queryFn: () => assetsService.getStats(id),
        enabled: !!id,
    });
    const wos = useQuery({
        queryKey: ["asset-wos", id],
        queryFn: () => assetsService.getWorkOrders(id, 25),
        enabled: !!id,
    });
    const customer = useQuery({
        queryKey: ["customer", detail.data?.customer_id],
        queryFn: () => customersService.get(detail.data.customer_id),
        enabled: !!detail.data?.customer_id,
    });

    const toggleStatus = useMutation({
        mutationFn: (next) => assetsService.setOperationalStatus(id, next),
        onSuccess: () => {
            toast.success("Situação operacional atualizada.");
            qc.invalidateQueries({ queryKey: ["asset", id] });
            qc.invalidateQueries({ queryKey: ["assets"] });
            qc.invalidateQueries({ queryKey: ["dashboard"] });
        },
        onError: (e) => toast.error(getErrorMessage(e, "Falha ao atualizar situação.")),
    });

    const [deleteOpen, setDeleteOpen] = React.useState(false);
    const deleteMut = useMutation({
        mutationFn: () => assetsService.remove(id),
        onSuccess: () => {
            toast.success("Equipamento excluído.");
            qc.invalidateQueries({ queryKey: ["assets"] });
            navigate("/assets", { replace: true });
        },
        onError: (e) => toast.error(getErrorMessage(e, "Falha ao excluir equipamento.")),
    });

    const a = detail.data;

    const photosByCat = React.useMemo(() => {
        const map = {};
        PHOTO_CATEGORIES.forEach((c) => (map[c] = []));
        (a?.photos || []).forEach((p) => {
            const cat = (p && p.category) || "Geral";
            (map[cat] = map[cat] || []).push(p);
        });
        return map;
    }, [a]);

    const opStatus = a?.operational_status || "operante";

    return (
        <div className="space-y-6">
            <PageHeader
                title={a?.internal_code || "Equipamento"}
                description={a ? `${a.category || "—"} · ${a.manufacturer || "—"} ${a.model || ""}` : ""}
                backTo="/assets"
                breadcrumb={[
                    { label: "Início", to: "/dashboard" },
                    { label: "Equipamentos", to: "/assets" },
                    { label: a?.internal_code || "Detalhes" },
                ]}
                actions={
                    <>
                        {a && isAdmin && (
                            <Button
                                variant="outline"
                                onClick={() =>
                                    toggleStatus.mutate(opStatus === "operante" ? "parado" : "operante")
                                }
                                disabled={toggleStatus.isPending}
                                className={opStatus === "parado"
                                    ? "border-success/40 text-success hover:bg-success/5"
                                    : "border-danger/40 text-danger hover:bg-danger/5"}
                                data-testid="asset-toggle-op-status"
                            >
                                <Power className="mr-2 h-4 w-4" />
                                {opStatus === "parado" ? "Marcar como Operante" : "Marcar como Parado"}
                            </Button>
                        )}
                        <RoleGate resource="assets" action="update">
                            <Button variant="outline"
                                onClick={() => navigate(`/assets/${id}/edit`)}
                                data-testid="asset-edit-button">
                                <Pencil className="mr-2 h-4 w-4" /> Editar
                            </Button>
                        </RoleGate>
                        <RoleGate resource="assets" action="delete">
                            <Button
                                variant="outline"
                                className="border-red-300 text-red-700 hover:bg-red-50 dark:border-red-500/50 dark:text-red-300 dark:hover:bg-red-500/10"
                                onClick={() => setDeleteOpen(true)}
                                data-testid="asset-delete-button"
                            >
                                <Trash2 className="mr-2 h-4 w-4" /> Excluir
                            </Button>
                        </RoleGate>
                    </>
                }
            />

            {detail.isLoading || !a ? (
                <div className="grid gap-6 lg:grid-cols-3">
                    <Skeleton className="h-64 lg:col-span-2" />
                    <Skeleton className="h-64" />
                </div>
            ) : (
                <div className="grid gap-6 lg:grid-cols-3">
                    <div className="space-y-6 lg:col-span-2">
                        {/* Situação rapida + KPIs */}
                        <Card className="rounded-xl border shadow-sm" data-testid="asset-headline-card">
                            <CardContent className="grid gap-4 pt-5 md:grid-cols-4">
                                <div>
                                    <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                                        Situação
                                    </div>
                                    <div className="pt-1">
                                        {opStatus === "operante" ? (
                                            <Badge variant="outline" className="border-success/40 text-success"
                                                data-testid="asset-op-status-badge">
                                                Operante
                                            </Badge>
                                        ) : (
                                            <Badge variant="outline" className="border-danger/40 text-danger"
                                                data-testid="asset-op-status-badge">
                                                <Power className="mr-1 h-3 w-3" /> Parado
                                            </Badge>
                                        )}
                                    </div>
                                </div>
                                <Info label="Total de OS" value={stats.data?.total_wos ?? "—"} />
                                <Info label="OS abertas" value={stats.data?.open_wos ?? "—"} />
                                <Info label="Última OS finalizada"
                                    value={stats.data?.last_finalized_at ? formatDate(stats.data.last_finalized_at) : "—"} />
                            </CardContent>
                        </Card>

                        {/* Identificação */}
                        <Card className="rounded-xl border shadow-sm">
                            <CardHeader className="flex flex-row items-center justify-between pb-2">
                                <CardTitle className="text-sm font-semibold">Informações</CardTitle>
                                <Badge variant="outline" className="text-[10px]">
                                    Cadastro: {formatDate(a.created_at)}
                                </Badge>
                            </CardHeader>
                            <CardContent className="grid gap-4 md:grid-cols-3">
                                <Info label="Código" value={<span className="font-mono">{a.internal_code}</span>} />
                                <Info label="Categoria" value={a.category} />
                                <Info label="Cliente" value={customer.data?.legal_name || "—"} />
                                <Info label="Fabricante" value={a.manufacturer} />
                                <Info label="Modelo" value={a.model} />
                                <Info label="Número de Série" value={a.serial_number} />
                                <Info label="Patrimônio do cliente" value={a.customer_asset_tag} />
                                <Info label="Ano de fabricação" value={a.manufacturing_year} />
                                {a.description && (
                                    <div className="md:col-span-3">
                                        <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                                            Descrição
                                        </div>
                                        <div className="text-sm text-foreground whitespace-pre-wrap">{a.description}</div>
                                    </div>
                                )}
                            </CardContent>
                        </Card>

                        {/* Dados Técnicos */}
                        <Card className="rounded-xl border shadow-sm">
                            <CardHeader className="pb-2">
                                <CardTitle className="text-sm font-semibold">Dados Técnicos</CardTitle>
                            </CardHeader>
                            <CardContent className="grid gap-4 md:grid-cols-3">
                                <Info label="Potência" value={a.power} />
                                <Info label="Tensão" value={a.voltage} />
                                <Info label="Corrente" value={a.current} />
                                <Info label="RPM" value={a.rpm} />
                                <Info label="Frequência" value={a.frequency} />
                                <Info label="Peso" value={a.weight} />
                            </CardContent>
                        </Card>

                        {/* Localização */}
                        {(a.unit || a.sector || a.installation_location) && (
                            <Card className="rounded-xl border shadow-sm">
                                <CardHeader className="pb-2 flex flex-row items-center gap-2">
                                    <MapPin className="h-4 w-4 text-muted-foreground" />
                                    <CardTitle className="text-sm font-semibold">Localização</CardTitle>
                                </CardHeader>
                                <CardContent className="grid gap-4 md:grid-cols-3">
                                    <Info label="Unidade" value={a.unit} />
                                    <Info label="Setor" value={a.sector} />
                                    <Info label="Local de instalação" value={a.installation_location} />
                                </CardContent>
                            </Card>
                        )}

                        {/* Fotos */}
                        <Card className="rounded-xl border shadow-sm">
                            <CardHeader className="pb-2">
                                <CardTitle className="text-sm font-semibold">Fotos</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <Tabs defaultValue={PHOTO_CATEGORIES[0]}>
                                    <TabsList className="flex-wrap">
                                        {PHOTO_CATEGORIES.map((c) => (
                                            <TabsTrigger key={c} value={c} data-testid={`view-photos-tab-${c}`}>
                                                {c}
                                                {photosByCat[c]?.length > 0 && (
                                                    <span className="ml-1.5 rounded-full bg-muted px-1.5 text-[10px] text-muted-foreground">
                                                        {photosByCat[c].length}
                                                    </span>
                                                )}
                                            </TabsTrigger>
                                        ))}
                                    </TabsList>
                                    {PHOTO_CATEGORIES.map((c) => (
                                        <TabsContent key={c} value={c} className="pt-3">
                                            {photosByCat[c]?.length === 0 ? (
                                                <div className="rounded-lg border border-dashed bg-muted/40 px-4 py-8 text-center text-xs text-muted-foreground">
                                                    Nenhuma foto nesta categoria.
                                                </div>
                                            ) : (
                                                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
                                                    {photosByCat[c].map((p, i) => (
                                                        <a key={p.id || p.url || `${c}-photo-${i}`} href={mediaUrl(p.url)}
                                                            target="_blank" rel="noopener noreferrer"
                                                            className="overflow-hidden rounded-xl border"
                                                            data-testid={`view-photo-${c}-${i}`}>
                                                            <AspectRatio ratio={4 / 3}>
                                                                <img src={mediaUrl(p.url)}
                                                                    alt={p.caption || `Foto ${i + 1}`}
                                                                    className="h-full w-full object-cover" />
                                                            </AspectRatio>
                                                        </a>
                                                    ))}
                                                </div>
                                            )}
                                        </TabsContent>
                                    ))}
                                </Tabs>
                            </CardContent>
                        </Card>

                        {/* Observações */}
                        {a.notes && (
                            <Card className="rounded-xl border shadow-sm">
                                <CardHeader className="pb-2">
                                    <CardTitle className="text-sm font-semibold">Observações</CardTitle>
                                </CardHeader>
                                <CardContent className="text-sm text-foreground whitespace-pre-wrap">
                                    {a.notes}
                                </CardContent>
                            </Card>
                        )}

                        {/* Histórico de OS (lista) */}
                        <Card className="rounded-xl border shadow-sm" data-testid="asset-wos-card">
                            <CardHeader className="flex flex-row items-center justify-between pb-2">
                                <div className="flex items-center gap-2">
                                    <ClipboardList className="h-4 w-4 text-muted-foreground" />
                                    <CardTitle className="text-sm font-semibold">
                                        Histórico de OS ({(wos.data || []).length})
                                    </CardTitle>
                                </div>
                                <span className="text-[11px] text-muted-foreground">Mais recentes primeiro</span>
                            </CardHeader>
                            <CardContent>
                                {wos.isLoading ? (
                                    <div className="space-y-2">
                                        {[0, 1].map((i) => <Skeleton key={`wo-skeleton-${i}`} className="h-10 w-full" />)}
                                    </div>
                                ) : (wos.data || []).length === 0 ? (
                                    <div className="rounded-lg border border-dashed bg-muted/40 px-4 py-8 text-center text-xs text-muted-foreground">
                                        Nenhuma OS registrada para este equipamento ainda.
                                    </div>
                                ) : (
                                    <ul className="divide-y">
                                        {(wos.data || []).map((w) => (
                                            <li key={w.id}
                                                onClick={() => navigate(`/work-orders/${w.id}`)}
                                                className="flex cursor-pointer items-center justify-between gap-3 py-3 hover:bg-accent/40"
                                                data-testid={`asset-wo-row-${w.number}`}>
                                                <div className="flex items-center gap-3 min-w-0">
                                                    <div className="grid h-9 w-9 place-items-center rounded-lg bg-muted text-muted-foreground">
                                                        <ClipboardList className="h-4 w-4" />
                                                    </div>
                                                    <div className="min-w-0">
                                                        <div className="font-mono text-sm font-medium">{w.number}</div>
                                                        <div className="truncate text-xs text-muted-foreground">
                                                            {w.title || "—"}
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <Badge variant="outline" className="text-[10px]">
                                                        {OS_PRIORITY_LABELS[w.priority] || w.priority}
                                                    </Badge>
                                                    <WoStatusBadge value={w.status} />
                                                    <span className="text-[11px] text-muted-foreground">
                                                        {formatDate(w.created_at)}
                                                    </span>
                                                </div>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </CardContent>
                        </Card>

                        {/* Linha do tempo (timeline) */}
                        <Card className="rounded-xl border shadow-sm" data-testid="asset-history-card">
                            <CardHeader className="flex flex-row items-center justify-between pb-2">
                                <div className="flex items-center gap-2">
                                    <History className="h-4 w-4 text-muted-foreground" />
                                    <CardTitle className="text-sm font-semibold">Linha do tempo</CardTitle>
                                </div>
                                <span className="text-[11px] text-muted-foreground">
                                    Cadastro → OS → Fotos → Intervenções
                                </span>
                            </CardHeader>
                            <CardContent>
                                <AssetTimeline assetId={id} />
                            </CardContent>
                        </Card>
                    </div>

                    {/* Lateral */}
                    <div className="space-y-6 lg:col-span-1">
                        <QRDisplay
                            qrUrl={a.qr_code_url}
                            publicUrl={a.public_url}
                            internalCode={a.internal_code}
                            downloadName={`qr-${a.internal_code}.png`}
                        />

                        <Card className="rounded-xl border shadow-sm">
                            <CardHeader className="pb-2">
                                <CardTitle className="text-sm font-semibold">Resumo de manutenção</CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-3 text-sm">
                                <div className="flex justify-between">
                                    <span className="text-muted-foreground">Total de OS</span>
                                    <span className="font-medium">{stats.data?.total_wos ?? "—"}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-muted-foreground">OS em aberto</span>
                                    <span className="font-medium">{stats.data?.open_wos ?? "—"}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-muted-foreground">Última OS</span>
                                    <span className="font-medium">
                                        {stats.data?.last_wo?.number || "—"}
                                    </span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-muted-foreground">Data</span>
                                    <span className="font-medium">
                                        {stats.data?.last_wo_at ? formatDateTime(stats.data.last_wo_at) : "—"}
                                    </span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-muted-foreground">Última manutenção finalizada</span>
                                    <span className="font-medium">
                                        {stats.data?.last_finalized_at ? formatDateTime(stats.data.last_finalized_at) : "—"}
                                    </span>
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                </div>
            )}

            <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Excluir equipamento?</AlertDialogTitle>
                        <AlertDialogDescription>
                            O equipamento <span className="font-mono font-medium">{a?.internal_code}</span> será marcado como excluído. Ordens de serviço e histórico permanecerão preservados.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="asset-delete-cancel">Manter</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={(e) => { e.preventDefault(); deleteMut.mutate(); }}
                            className="bg-red-600 hover:bg-red-700 text-white"
                            data-testid="asset-delete-confirm"
                        >
                            Excluir equipamento
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
